import { 
    IonButton, 
    IonCol, 
    IonGrid, 
    IonInput, 
    IonItem, 
    IonLabel, 
    IonRow, 
    IonSpinner, 
    IonTextarea,
    IonToast,
    IonIcon,
    IonPopover,
    useIonViewDidEnter 
} from '@ionic/react';
import { cloudDownload, cloudUpload, informationCircleOutline } from 'ionicons/icons';
import React, { useRef, useState } from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import { apiBaseUrl } from '../../configs/app';
import { useAppContext } from '../../services/app-context.service';
import Settings from '../../types/settngs.type';

const SettingsPage = () => {
    const store = useAppContext() as any;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [config, setConfig] = useState<Settings>({
        company_email: '',
        company_phone: '',
        company_address: '',
        bdt_per_rmb: 0,
        profit_per_sale: 0,
        min_order_amount: 1000,
        min_order_item_count: 3,
        advance_payment_percent: 70,
        shipping_instruction_1: '',
        shipping_instruction_2: '',
        shipping_instruction_3: '',
        shipping_instruction_4: '',
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastColor, setToastColor] = useState('success');
    const [helpPopover, setHelpPopover] = useState<{ isOpen: boolean; event?: Event; label: string; description: string; example: string }>({
        isOpen: false,
        label: '',
        description: '',
        example: ''
    });

    const openHelpPopover = (event: any, label: string, description: string, example: string) => {
        setHelpPopover({
            isOpen: true,
            event: event?.nativeEvent || event,
            label,
            description,
            example,
        });
    };

    const closeHelpPopover = () => {
        setHelpPopover((prev) => ({ ...prev, isOpen: false }));
    };

    const pricingHelpButton = (label: string, description: string, example: string) => (
        <IonButton
            slot="end"
            fill="clear"
            size="small"
            aria-label={`Help for ${label}. ${description} Example: ${example}`}
            onClick={(e) => openHelpPopover(e, label, description, example)}
        >
            <IonIcon icon={informationCircleOutline} />
        </IonButton>
    );

    const fetchConfig = () => {
        setLoading(true);
        const myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");
        myHeaders.append("Authorization", "Bearer " + store.user.token);

        const requestOptions = {
            method: 'GET',
            headers: myHeaders,
        } as any;

        fetch(apiBaseUrl + "admin/frontend-config", requestOptions)
            .then((res) => res.json())
            .then((data) => {
                setConfig(data);
            })
            .catch((err) => {
                console.log(err);
                setToastMessage('Failed to load configuration');
                setToastColor('danger');
                setShowToast(true);
            })
            .finally(() => setLoading(false));
    };

    useIonViewDidEnter(() => {
        fetchConfig();
    });

    const handleSave = () => {
        setSaving(true);
        const myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Authorization", "Bearer " + store.user.token);

        const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: JSON.stringify(config),
        } as any;

        fetch(apiBaseUrl + "admin/frontend-config", requestOptions)
            .then((res) => res.json())
            .then((data) => {
                if (data.message) {
                    setToastMessage(data.message);
                    setToastColor('success');
                    setShowToast(true);
                    // Reload the config from server
                    fetchConfig();
                } else if (data.errors) {
                    setToastMessage('Validation errors occurred');
                    setToastColor('danger');
                    setShowToast(true);
                }
            })
            .catch((err) => {
                console.log(err);
                setToastMessage('Failed to save configuration');
                setToastColor('danger');
                setShowToast(true);
            })
            .finally(() => setSaving(false));
    };

    const handleInputChange = (field: keyof Settings, value: any) => {
        setConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleExport = () => {
        const dataStr = JSON.stringify(config, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `frontend-config-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        setToastMessage('Configuration exported successfully');
        setToastColor('success');
        setShowToast(true);
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.json')) {
            setToastMessage('Please select a valid JSON file');
            setToastColor('danger');
            setShowToast(true);
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const importedConfig = JSON.parse(e.target?.result as string);
                
                // Validate the imported config has required fields
                const requiredFields: (keyof Settings)[] = [
                    'company_email',
                    'company_phone',
                    'company_address',
                    'bdt_per_rmb',
                    'profit_per_sale',
                    'min_order_amount',
                    'min_order_item_count',
                    'advance_payment_percent',
                    'shipping_instruction_1',
                    'shipping_instruction_2',
                    'shipping_instruction_3',
                    'shipping_instruction_4'
                ];
                
                const missingFields = requiredFields.filter(field => !(field in importedConfig));
                
                if (missingFields.length > 0) {
                    setToastMessage(`Missing fields: ${missingFields.join(', ')}`);
                    setToastColor('danger');
                    setShowToast(true);
                    return;
                }
                
                setConfig(importedConfig);
                setToastMessage('Configuration imported successfully. Click Save to apply changes.');
                setToastColor('warning');
                setShowToast(true);
            } catch (error) {
                setToastMessage('Failed to parse JSON file. Please check the file format.');
                setToastColor('danger');
                setShowToast(true);
            }
        };
        
        reader.readAsText(file);
        
        // Reset the input so the same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <AdminLayout title="Frontend Settings">
            <IonGrid>
                <IonRow>
                    <IonCol size="12" sizeMd="10" offsetMd="1" sizeLg="8" offsetLg="2">
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                                <IonSpinner />
                            </div>
                        ) : (
                            <>
                                <div style={{ padding: '20px 0', display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <IonButton color="secondary" onClick={handleExport}>
                                        <IonIcon slot="start" icon={cloudDownload} />
                                        Export JSON
                                    </IonButton>
                                    <IonButton color="tertiary" onClick={handleImportClick}>
                                        <IonIcon slot="start" icon={cloudUpload} />
                                        Import JSON
                                    </IonButton>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".json,application/json"
                                        onChange={handleImport}
                                        style={{ display: 'none' }}
                                    />
                                </div>
                                
                                <h2>Company Information</h2>
                                <IonItem>
                                        <IonLabel position="floating">Company Email</IonLabel>
                                        <IonInput
                                            type="email"
                                            value={config.company_email}
                                            onIonChange={(e) => handleInputChange('company_email', e.detail.value)}
                                        />
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel position="floating">Company Phone</IonLabel>
                                        <IonInput
                                            type="tel"
                                            value={config.company_phone}
                                            onIonChange={(e) => handleInputChange('company_phone', e.detail.value)}
                                        />
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel position="floating">Company Address</IonLabel>
                                        <IonTextarea
                                            rows={3}
                                            value={config.company_address}
                                            onIonChange={(e) => handleInputChange('company_address', e.detail.value)}
                                        />
                                    </IonItem>

                                    <h2 style={{ marginTop: '30px' }}>Pricing Configuration</h2>
                                    <IonItem>
                                        <IonLabel position="floating">BDT per RMB</IonLabel>
                                        <IonInput
                                            type="number"
                                            value={config.bdt_per_rmb}
                                            onIonChange={(e) => handleInputChange('bdt_per_rmb', parseFloat(e.detail.value || '0'))}
                                        />
                                        {pricingHelpButton(
                                            'BDT per RMB',
                                            'Exchange rate used to convert product prices from RMB to BDT for all pricing calculations.',
                                            'If 1 RMB equals 122.5 BDT, enter 122.5'
                                        )}
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel position="floating">Profit per Sale (Multiplier)</IonLabel>
                                        <IonInput
                                            type="number"
                                            step="0.01"
                                            value={config.profit_per_sale}
                                            onIonChange={(e) => handleInputChange('profit_per_sale', parseFloat(e.detail.value || '0'))}
                                        />
                                        {pricingHelpButton(
                                            'Profit per Sale (Multiplier)',
                                            'Markup multiplier applied after base conversion. Values above 1 add margin.',
                                            '1.15 means a 15% markup, 1.00 means no markup'
                                        )}
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel position="floating">Minimum Order Amount</IonLabel>
                                        <IonInput
                                            type="number"
                                            step="0.01"
                                            value={config.min_order_amount}
                                            onIonChange={(e) => handleInputChange('min_order_amount', parseFloat(e.detail.value || '0'))}
                                        />
                                        {pricingHelpButton(
                                            'Minimum Order Amount',
                                            'Minimum cart subtotal required to place an order, in BDT.',
                                            '1000 means customer subtotal must be at least 1000 BDT'
                                        )}
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel position="floating">Minimum Order Item Count</IonLabel>
                                        <IonInput
                                            type="number"
                                            value={config.min_order_item_count}
                                            onIonChange={(e) => handleInputChange('min_order_item_count', parseInt(e.detail.value || '0'))}
                                        />
                                        {pricingHelpButton(
                                            'Minimum Order Item Count',
                                            'Minimum total quantity of items required in a single order.',
                                            '3 means at least 3 items are required to checkout'
                                        )}
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel position="floating">Advance Payment Percent</IonLabel>
                                        <IonInput
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            value={config.advance_payment_percent}
                                            onIonChange={(e) => handleInputChange('advance_payment_percent', parseFloat(e.detail.value || '0'))}
                                        />
                                        {pricingHelpButton(
                                            'Advance Payment Percent',
                                            'Percentage of the grand total that must be paid upfront before processing.',
                                            '70 means customer must pay 70% in advance'
                                        )}
                                    </IonItem>

                                    <h2 style={{ marginTop: '30px' }}>Shipping Instructions</h2>
                                    <IonItem>
                                        <IonLabel position="floating">Shipping Instruction 1</IonLabel>
                                        <IonTextarea
                                            rows={2}
                                            value={config.shipping_instruction_1}
                                            onIonChange={(e) => handleInputChange('shipping_instruction_1', e.detail.value)}
                                        />
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel position="floating">Shipping Instruction 2 (Category A)</IonLabel>
                                        <IonTextarea
                                            rows={3}
                                            value={config.shipping_instruction_2}
                                            onIonChange={(e) => handleInputChange('shipping_instruction_2', e.detail.value)}
                                        />
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel position="floating">Shipping Instruction 3 (Category B)</IonLabel>
                                        <IonTextarea
                                            rows={3}
                                            value={config.shipping_instruction_3}
                                            onIonChange={(e) => handleInputChange('shipping_instruction_3', e.detail.value)}
                                        />
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel position="floating">Shipping Instruction 4 (Category C)</IonLabel>
                                        <IonTextarea
                                            rows={3}
                                            value={config.shipping_instruction_4}
                                            onIonChange={(e) => handleInputChange('shipping_instruction_4', e.detail.value)}
                                        />
                                    </IonItem>

                                    <div style={{ padding: '20px', textAlign: 'center' }}>
                                        <IonButton 
                                            expand="block" 
                                            onClick={handleSave}
                                            disabled={saving}
                                        >
                                            {saving ? <IonSpinner /> : 'Save Configuration'}
                                        </IonButton>
                                    </div>
                                </>
                            )}
                        </IonCol>
                    </IonRow>
                </IonGrid>

                <IonToast
                    isOpen={showToast}
                    onDidDismiss={() => setShowToast(false)}
                    message={toastMessage}
                    duration={3000}
                    color={toastColor}
                />

                <IonPopover
                    isOpen={helpPopover.isOpen}
                    event={helpPopover.event}
                    onDidDismiss={closeHelpPopover}
                    showBackdrop={false}
                    side="top"
                    alignment="center"
                >
                    <div style={{ padding: '10px 12px', maxWidth: '240px' }}>
                        <strong>{helpPopover.label}</strong>
                        <div style={{ marginTop: '6px', fontSize: '13px', lineHeight: 1.35 }}>
                            {helpPopover.description}
                        </div>
                        <div style={{ marginTop: '6px', fontSize: '13px' }}>
                            Example: {helpPopover.example}
                        </div>
                    </div>
                </IonPopover>
            </AdminLayout>
        );
    };
    
    export default SettingsPage;